import React, { Dispatch, useState, useRef } from 'react';
import { Stack, Autocomplete, TextField } from '@mui/material';
import {
  StyledModal,
  FormTextField,
  ModalFooterButtons,
  sharedClasses
} from '@/components/NewUI/Components';
import { CandidatesAction, CandidatesState, FormFieldProps, TCandidate } from './types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Api from './API';
import { ICountries, IStates } from '../Components/sharedTypes';
import { styles } from './styles';
import { CandidateAction, CandidateState } from '../Candidate/types';
import { streetTypes } from './config';
import { scrollToElement } from '../utils/scroll-to-element';
import { renderAutocomplete } from './utils/autocompleteUtils';

const FormField = ({
  label,
  value,
  onChange,
  required = false,
  error,
  innerRef,
  helperText,
  fullWidth = true
}: FormFieldProps) => (
  <FormTextField
    label={label}
    value={value}
    onChange={(v) => onChange(v.target.value)}
    required={required}
    fullWidth={fullWidth}
    error={error}
    innerRef={innerRef}
    helperText={helperText}
  />
);

export const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const EditCandidate = ({
  candidates,
  editedCandidate,
  CandidateState,
  CandidateDispatch,
  CandidatesState,
  CandidatesDispatch
}: {
  candidates?: { res: { candidates: TCandidate[] }; resHead: any };
  editedCandidate: TCandidate;
  CandidateState?: CandidateState;
  CandidateDispatch?: Dispatch<CandidateAction>;
  CandidatesState?: CandidatesState;
  CandidatesDispatch?: Dispatch<CandidatesAction>;
}) => {
  const [country, setCountry] = useState<ICountries | null>(null);
  const [state, setState] = useState<IStates | null>(null);
  const [candidate, setCandidate] = useState<TCandidate>(editedCandidate);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const fieldRefs = {
    firstname: useRef<HTMLInputElement>(null),
    lastname: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    linkedin: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null),
    mobile: useRef<HTMLInputElement>(null),
    address: useRef<HTMLInputElement>(null),
    suburb: useRef<HTMLInputElement>(null),
    postcode: useRef<HTMLInputElement>(null),
    country: useRef<HTMLInputElement>(null),
    state: useRef<HTMLInputElement>(null)
  };

  const updateAddressField = (field: string, value: string) => {
    setCandidate({
      ...candidate,
      address_details: { ...candidate.address_details, [field]: value }
    });
  };

  const updateCandidateField = (field: string, value: string) => {
    setCandidate({ ...candidate, [field]: value });
  };

  const validateLinkedInURL = (url: string) => {
    return String(url)
      .toLowerCase()
      .match(/^(http(s)?:\/\/)?([\w]+\.)?linkedin\.com\/(pub|in|profile)\/([-a-zA-Z0-9]+)\/*/gm);
  };

  const validateInputs = () => {
    setErrors({});
    const inputErrors: Record<string, string> = {};
    if (!candidate.firstname.trim()) inputErrors.firstname = 'First name can not be empty';
    if (!candidate.lastname.trim()) inputErrors.lastname = 'Last name can not be empty';
    if (!validateEmail(candidate.email)) inputErrors.email = 'Please enter a proper email address';
    if (candidate.linkedin_url && !validateLinkedInURL(candidate.linkedin_url))
      inputErrors.linkedin = 'Please enter a proper LinkedIn URL';
    if (!candidate.phone1.trim()) {
      inputErrors.phone = 'Phone can not be empty';
    } else if (!Number(candidate.phone1)) {
      inputErrors.phone = 'Please enter a proper phone number';
    }
    if (candidate.phone2?.trim() && !Number(candidate.phone2))
      inputErrors.mobile = 'Please enter a proper mobile number';
    if (!candidate?.address_details?.street1?.trim())
      inputErrors.address = 'Street address can not be empty';
    if (!candidate?.address_details?.suburb?.trim())
      inputErrors.suburb = 'City, town or suburb can not be empty';
    if (!candidate?.address_details?.postcode?.trim())
      inputErrors.postcode = 'Postcode can not be empty';
    if (!country) inputErrors.country = 'Country can not be empty';
    if (!state) inputErrors.state = 'State can not be empty';
    setErrors(inputErrors);
    if (inputErrors.firstname) {
      scrollToElement(fieldRefs.firstname);
    } else if (inputErrors.lastname) {
      scrollToElement(fieldRefs.lastname);
    } else if (inputErrors.email) {
      scrollToElement(fieldRefs.email);
    } else if (inputErrors.linkedin) {
      scrollToElement(fieldRefs.linkedin);
    } else if (inputErrors.phone) {
      scrollToElement(fieldRefs.phone);
    } else if (inputErrors.mobile) {
      scrollToElement(fieldRefs.mobile);
    } else if (inputErrors.address) {
      scrollToElement(fieldRefs.address);
    } else if (inputErrors.suburb) {
      scrollToElement(fieldRefs.suburb);
    } else if (inputErrors.postcode) {
      scrollToElement(fieldRefs.postcode);
    } else if (inputErrors.country) {
      scrollToElement(fieldRefs.country);
    } else if (inputErrors.state) {
      scrollToElement(fieldRefs.state);
    }
    return !Object.keys(inputErrors).length;
  };

  const { data: countriesStates } = useQuery({
    queryKey: ['countries states'],
    queryFn: async () => {
      const { res } = await Api.getCountriesStates();
      return res as ICountries[];
    },
    onSuccess: (res: ICountries[]) => {
      setCountry(
        res.find((r: ICountries) => r.id === candidate?.address_details?.country?.id) || null
      );
      setState(
        res
          .find((r: ICountries) => r.id === candidate?.address_details?.country?.id)
          ?.states?.find((s: IStates) => s.id === candidate?.address_details?.state?.id) || null
      );
    },
    onError: (error) => {
      const errorSnackbarPayload: {
        type: 'SET_SNACKBAR';
        payload: { message: string; state: 'error' };
      } = {
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting countries and states data, ${error}`,
          state: 'error'
        }
      };
      CandidatesDispatch && CandidatesDispatch(errorSnackbarPayload);
      CandidateDispatch && CandidateDispatch(errorSnackbarPayload);
    }
  });

  const candidateData = {
    firstname: candidate.firstname,
    lastname: candidate.lastname,
    employee_id: candidate.employee_id,
    email: candidate.email,
    linkedin_url: candidate.linkedin_url,
    phone1: candidate.phone1,
    phone2: candidate.phone2
  };

  const candidateAddress = {
    street1: candidate?.address_details?.street1,
    street2: candidate?.address_details?.street2,
    suburb: candidate?.address_details?.suburb,
    postcode: candidate?.address_details?.postcode,
    street_number: candidate?.address_details?.street_number || '',
    street_type: candidate?.address_details?.street_type
  };

  const { mutate: updateCandidate, isLoading: updatingCandidate } = useMutation({
    mutationFn: (arg: { countryId: number; stateId: number }) =>
      Api.updateCandidate(candidate.id, {
        ...candidateData,
        address_attributes: {
          ...candidateAddress,
          country_id: arg.countryId,
          state_id: arg.stateId
        }
      }),
    onError: (error: { error: string }) => {
      const errorSnackbarPayload: {
        type: 'SET_SNACKBAR';
        payload: { message: string; state: 'error' };
      } = {
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error updating candidate, ${error.error}`,
          state: 'error'
        }
      };
      CandidatesDispatch && CandidatesDispatch(errorSnackbarPayload);
      CandidateDispatch && CandidateDispatch(errorSnackbarPayload);
    },
    onSuccess: () => {
      if (!country || !state) return;
      const successSnackbarPayload: {
        type: 'SET_SNACKBAR';
        payload: { message: string; state: 'success' };
      } = {
        type: 'SET_SNACKBAR',
        payload: { message: `Candidate has been updated.`, state: 'success' }
      };
      CandidatesDispatch && CandidatesDispatch(successSnackbarPayload);
      CandidateDispatch && CandidateDispatch(successSnackbarPayload);
      if (candidates) {
        const newCandidates = [...candidates.res.candidates];
        const index = newCandidates.findIndex((e) => e.id === candidate.id);
        newCandidates[index] = {
          ...newCandidates[index],
          ...candidateData,
          address_details: {
            ...newCandidates[index].address_details,
            ...candidateAddress,
            country: {
              ...newCandidates[index].address_details.country,
              id: country.id,
              name: country.name
            },
            state: state
          }
        };
        queryClient.setQueryData(
          ['candidates', CandidatesState?.candidatesTableSettings, CandidatesState?.searchQuery],
          {
            ...candidates,
            res: { candidates: newCandidates }
          }
        );
        queryClient.invalidateQueries(['candidates']);
        CandidatesDispatch && CandidatesDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'edit' });
      } else {
        const updatedCandidate = {
          ...editedCandidate,
          ...candidateData,
          address_details: {
            ...editedCandidate.address_details,
            ...candidateAddress,
            country: country,
            state: state
          }
        };
        queryClient.setQueryData(['candidate'], updatedCandidate);
        queryClient.invalidateQueries(['candidate']);
        CandidateDispatch && CandidateDispatch({ type: 'OPEN_EDIT_MODAL', payload: false });
      }
    }
  });

  return (
    <StyledModal
      isOpen={
        !!CandidatesState?.candidatesTableState.actions.isOpen.edit ||
        !!CandidateState?.openEditModal
      }
      label="Edit Candidate"
      handleClose={() => {
        CandidatesDispatch && CandidatesDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'edit' });
        CandidateDispatch && CandidateDispatch({ type: 'OPEN_EDIT_MODAL', payload: false });
      }}
      styleOverrides={{ maxHeight: '625px', maxWidth: '800px', padding: '40px' }}
      closeIconId="close-edit-candidate-button"
    >
      <Stack sx={{ width: '100%', height: '100%', rowGap: 4 }}>
        <Stack sx={{ fontWeight: 'bold', color: '#084D6D', fontSize: '25px' }}>
          {editedCandidate.firstname} {editedCandidate.lastname}
        </Stack>
        <Stack sx={styles.editCandidateModalContent}>
          <Stack sx={{ rowGap: 2 }}>
            <Stack sx={styles.editCandidateModalDetailsHeader}>Candidate Details</Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="First name"
                value={candidate.firstname}
                onChange={(v) => updateCandidateField('firstname', v)}
                error={errors.firstname}
                innerRef={fieldRefs.firstname}
              />
              <FormField
                label="Last name"
                value={candidate.lastname}
                onChange={(v) => updateCandidateField('lastname', v)}
                error={errors.lastname}
                innerRef={fieldRefs.lastname}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="Employee ID"
                value={candidate.employee_id?.toString() || ''}
                onChange={(v) => updateCandidateField('employee_id', v)}
              />
              <FormField
                label="LinkedIn URL"
                value={candidate.linkedin_url || ''}
                onChange={(v) => updateCandidateField('linkedin_url', v)}
                error={errors.linkedin}
                innerRef={fieldRefs.linkedin}
              />
            </Stack>
          </Stack>
          <Stack sx={{ rowGap: 2 }}>
            <Stack sx={styles.editCandidateModalDetailsHeader}>Contact Details</Stack>
            <Stack>
              <FormField
                label="Email"
                value={candidate.email}
                onChange={(v) => updateCandidateField('email', v)}
                error={errors.email}
                innerRef={fieldRefs.email}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="Phone"
                value={candidate.phone1}
                onChange={(v) => updateCandidateField('phone1', v)}
                fullWidth
                required
                helperText="Digits only or add + for international numbers"
                error={errors.phone}
                innerRef={fieldRefs.phone}
              />
              <FormField
                label="Mobile"
                value={candidate.phone2}
                onChange={(v) => updateCandidateField('phone2', v)}
                fullWidth
                helperText="Digits only or add + for international numbers"
                error={errors.mobile}
                innerRef={fieldRefs.mobile}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="Street address"
                value={candidate?.address_details?.street1 || ''}
                onChange={(v) => updateAddressField('street1', v)}
                required
                error={errors.address}
                innerRef={fieldRefs.address}
              />
              <FormField
                label="Street address cont'd"
                value={candidate?.address_details?.street2 || ''}
                onChange={(v) => updateAddressField('street2', v)}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="Street number"
                value={candidate?.address_details?.street_number || ''}
                onChange={(v) => updateAddressField('street_number', v)}
              />
              <Autocomplete
                disablePortal
                value={candidate?.address_details?.street_type}
                options={streetTypes}
                getOptionLabel={(option) => option}
                onChange={(_event, newValue) => updateAddressField('street_type', newValue || '')}
                sx={{ ...sharedClasses.formAutocomplete, ...styles.streetTypesAutocomplete }}
                slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Street type"
                    slotProps={{ inputLabel: { shrink: true } }}
                    placeholder="Please select"
                    sx={styles.editCandidateAutocompleteTextfield}
                  />
                )}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              <FormField
                label="City, town or suburb"
                value={candidate?.address_details?.suburb || ''}
                onChange={(v) => updateAddressField('suburb', v)}
                required
                error={errors.suburb}
                innerRef={fieldRefs.suburb}
              />
              <FormField
                label="Postcode or zipcode"
                value={candidate?.address_details?.postcode || ''}
                onChange={(v) => updateAddressField('postcode', v)}
                required
                error={errors.postcode}
                innerRef={fieldRefs.postcode}
              />
            </Stack>
            <Stack sx={styles.editCandidateModalDetailsRows}>
              {renderAutocomplete<ICountries>(
                'Country',
                country,
                countriesStates || [],
                (newValue) => {
                  if (newValue) {
                    const newErrors = { ...errors };
                    delete newErrors.country;
                    setErrors(newErrors);
                  }
                  setCountry(newValue);
                  setState(null);
                },
                false,
                !country,
                errors.country,
                fieldRefs.country
              )}
              {renderAutocomplete<IStates>(
                'State, region or province',
                state,
                country?.states || [],
                (newValue) => {
                  if (newValue) {
                    const newErrors = { ...errors };
                    delete newErrors.state;
                    setErrors(newErrors);
                  }
                  setState(newValue);
                },
                !country,
                !state,
                errors.state,
                fieldRefs.state
              )}
            </Stack>
          </Stack>
        </Stack>
        <ModalFooterButtons
          primaryButtonText="Save"
          primaryButtonCallback={() => {
            if (validateInputs() && country && state)
              updateCandidate({ countryId: country.id, stateId: state.id });
          }}
          primaryButtonID="save-candidate-button"
          secondaryButtonText="Cancel"
          secondaryButtonCallback={() => {
            CandidatesDispatch &&
              CandidatesDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'edit' });
            CandidateDispatch && CandidateDispatch({ type: 'OPEN_EDIT_MODAL', payload: false });
          }}
          secondaryButtonID="cancel-edit-candidate-button"
          isLoading={updatingCandidate}
        />
      </Stack>
    </StyledModal>
  );
};

export default EditCandidate;
